<template>
  <RevInfoBlock
    v-if="hasMissingMarketsOrCategories"
    :icon="IconWarning"
    :title="i18n(translations.missingMarketsOrCategories)"
    variant="warning"
  >
    <div class="flex items-center gap-16">
      <div class="flex flex-wrap gap-6">
        <div v-for="{ market } in missingMarkets" :key="market">
          <FlagDisplayer :market="market as MarketCountryCode" />
        </div>
      </div>
      <div class="flex flex-wrap gap-16">
        <RevTag
          v-for="category in missingCategoryNames"
          :key="category"
          :label="category"
          variant="secondary"
        />
      </div>
    </div>
  </RevInfoBlock>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api'
import type { ShippingCategory } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import type { MarketWithSubMarkets } from '@backmarket/http-api/src/api-specs-shipping/rules'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevTag } from '@ds/components/Tag'
import { IconWarning } from '@ds/icons/IconWarning'

import type { ShippingCategoryGroupRules } from '../../CategoryList.types'
import FlagDisplayer from '../FlagDisplayer/FlagDisplayer.vue'

import translations from './InfoBlocks.translations'

const props = defineProps<{
  availableMarkets: MarketWithSubMarkets[]
  availableCategories: ShippingCategory[]
  categoryGroupRules: ShippingCategoryGroupRules
}>()

const i18n = useI18n()

// missing markets take account only of market and not sub markets,
// since a rule cannot be saved if there is no free method for all sub markets
const missingMarkets = computed(() =>
  props.availableMarkets.filter(
    ({ market }) =>
      !props.categoryGroupRules.markets.find(({ market: m }) => m === market),
  ),
)

const missingCategoryNames = computed(() =>
  props.availableCategories.reduce((arr, { id, name }) => {
    if (!props.categoryGroupRules.categories.includes(id)) {
      arr.push(name)
    }

    return arr
  }, [] as string[]),
)

const hasMissingMarketsOrCategories = computed(
  () =>
    props.categoryGroupRules.rules.length &&
    (missingMarkets.value.length > 0 || missingCategoryNames.value.length > 0),
)
</script>
